import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Download, FileSearch, Loader2, RefreshCw } from 'lucide-react';
import type { WorkflowJob } from '../../graphql/types';
import { buildReportCsv, csvFileName } from '../../titleAnalysis';
import { EmptyState, RunningState, UseCaseSelect } from '../../components/ui';
import {
  RUN_ACTIVE_REASON,
  isRunBusy,
  isRunDisabled,
  isRunInProgress,
  runControlLabel,
  runDisabledReason,
} from './runControl';
import { useTitleAnalysisRun } from './useTitleAnalysisRun';
import TitleAnalysisReportBody from './TitleAnalysisReportBody';
import RulesHelp from './RulesHelp';

/**
 * TitleAnalysisMode (Title Search): ink header bar with the use case
 * drop-down, "Download PDF" and "Download CSV" (when a report exists) and the Run button in
 * Turbo, then a body that follows the run phase.
 *
 * The run state is the `TitleAnalysisSection` phase machine (`useTitleAnalysisRun`):
 * results are polled every 5 seconds while running, a run that outlives its
 * stale window becomes `stale`, and an earlier report stays visible after a
 * failed or stale run. Light theme only.
 *
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5.
 */
export interface TitleAnalysisModeProps {
  job: WorkflowJob;
  /**
   * Called when a run starts, completes or fails, so the Workspace can re-read
   * the job and show the new Title analysis status in the Rail and Top_Bar.
   */
  onRunStatusChange?: () => void;
}

const DID_NOT_FINISH = 'The analysis did not finish. Select Retry to run it again.';
const STALE = 'The previous run did not finish. Select Retry to run it again.';
const LOAD_ERROR = 'The title analysis could not be loaded. Check your connection and try again.';
const PDF_ERROR = 'The PDF could not be generated. Try again.';

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const TitleAnalysisMode: React.FC<TitleAnalysisModeProps> = ({ job, onRunStatusChange }) => {
  const { phase, results, selectedUseCase, setSelectedUseCase, startError, notice, run, reload } =
    useTitleAnalysisRun(job);

  // The run status is stored on the job, but the job is not pushed when a run
  // changes it, so ask the Workspace to re-read it at each change.
  const previousPhase = useRef(phase);
  useEffect(() => {
    const was = previousPhase.current;
    previousPhase.current = phase;
    if (was !== phase && (phase === 'running' || phase === 'completed' || phase === 'failed')) {
      onRunStatusChange?.();
    }
    // Only react to the phase changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const report = results?.report ?? null;
  const hasReport = report !== null;
  const inProgress = isRunInProgress(phase);
  const runDisabled = isRunDisabled(selectedUseCase, phase);
  const disabledReason = runDisabled ? runDisabledReason(selectedUseCase, phase) : null;
  const label = runControlLabel(phase, hasReport);

  const handleDownload = useCallback(() => {
    if (!report) return;
    const blob = new Blob([buildReportCsv(report, job.loanNumber)], {
      type: 'text/csv;charset=utf-8',
    });
    triggerDownload(blob, csvFileName(job.loanNumber, report.analysis_date));
  }, [report, job.loanNumber]);

  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    if (!report || generatingPdf) return;
    setPdfError(null);
    setGeneratingPdf(true);
    try {
      // Loaded on demand so the PDF libraries stay out of the main bundle.
      const { buildTitleAnalysisPdf, pdfFileName } = await import(
        '../../components/titleAnalysis/exportPdf'
      );
      const blob = buildTitleAnalysisPdf(report, job).output('blob');
      triggerDownload(blob, pdfFileName(job.loanNumber, report.analysis_date));
    } catch (err) {
      console.error('[TitleAnalysisMode] Failed to generate PDF:', err);
      if (mountedRef.current) setPdfError(PDF_ERROR);
    } finally {
      if (mountedRef.current) setGeneratingPdf(false);
    }
  }, [report, job, generatingPdf]);

  const showLoadError = phase === 'loadError' || (phase === 'completed' && !report);

  return (
    <div data-testid="title-analysis-mode" className="flex max-w-[1300px] flex-col gap-4">
      <section
        aria-label="Title analysis run"
        data-testid="title-analysis-header"
        className="rounded-xl bg-[#1c1428] px-[18px] py-3.5"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="title-analysis-use-case"
              className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#b8afd0]"
            >
              Use case
            </label>
            <UseCaseSelect
              id="title-analysis-use-case"
              variant="onInk"
              value={selectedUseCase}
              onChange={setSelectedUseCase}
              disabled={isRunBusy(phase)}
              disabledReason={RUN_ACTIVE_REASON}
              className="min-w-[240px]"
            />
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {hasReport ? (
              <>
                <button
                  type="button"
                  data-testid="title-analysis-download-pdf"
                  disabled={generatingPdf}
                  onClick={() => void handleDownloadPdf()}
                  className="inline-flex h-9 items-center gap-1.5 rounded-[9px] border border-white/25 bg-white/10 px-3.5 text-sm font-bold text-white hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white/10"
                >
                  {generatingPdf ? (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} aria-hidden="true" />
                  ) : (
                    <Download className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                  )}
                  {generatingPdf ? 'Preparing PDF…' : 'Download PDF'}
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex h-9 items-center gap-1.5 rounded-[9px] border border-white/25 bg-white/10 px-3.5 text-sm font-bold text-white hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <Download className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                  Download CSV
                </button>
              </>
            ) : null}
            <button
              type="button"
              data-testid="title-analysis-run"
              disabled={runDisabled}
              title={disabledReason ?? undefined}
              onClick={() => void run()}
              className="inline-flex h-9 items-center gap-1.5 rounded-[9px] bg-[#F0E100] px-4 text-sm font-bold text-[#1c1428] hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1c1428] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-100"
            >
              {inProgress ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} aria-hidden="true" />
              ) : null}
              {label}
            </button>
            <RulesHelp variant="onInk" />
          </div>
        </div>
        {!selectedUseCase ? (
          <p data-testid="title-analysis-run-hint" className="mt-2 text-[13px] text-[#c8c0dd]">
            {disabledReason}
          </p>
        ) : null}
      </section>

      {startError ? <Notice tone="error" message={startError} /> : null}
      {pdfError ? <Notice tone="error" message={pdfError} /> : null}
      {phase === 'running' && notice ? <Notice tone="info" message={notice} /> : null}
      {phase === 'failed' ? <Notice tone="error" message={DID_NOT_FINISH} /> : null}
      {phase === 'stale' ? <Notice tone="error" message={STALE} /> : null}
      {showLoadError ? (
        <Notice tone="error" message={LOAD_ERROR} onRetry={() => void reload()} />
      ) : null}
      {hasReport && !inProgress && phase !== 'completed' && phase !== 'loading' ? (
        <p role="status" className="text-[13px] text-gray-600">
          Showing the report from the last successful run.
        </p>
      ) : null}
      {hasReport && inProgress ? (
        <p role="status" className="text-[13px] text-gray-600">
          Updating: showing the previous report until the new analysis finishes.
        </p>
      ) : null}

      {/* Body by phase */}
      {phase === 'loading' && !hasReport ? (
        <div
          role="status"
          className="flex items-center justify-center gap-3 py-10 text-sm text-gray-600"
        >
          <Loader2 className="h-5 w-5 animate-spin text-[#834DFB]" aria-hidden="true" />
          Loading title analysis…
        </div>
      ) : null}

      {inProgress ? (
        <RunningState
          title={phase === 'starting' ? 'Starting title analysis…' : 'Title analysis is running'}
          description="This usually takes a few minutes. The results appear here when it finishes."
        />
      ) : null}

      {phase === 'idle' && !hasReport ? (
        <EmptyState
          icon={<FileSearch className="h-5 w-5" aria-hidden="true" />}
          title="No title analysis yet"
          description="Choose a use case, then run the analysis. It grades the title, derives lien position and lists exceptions with curative actions."
        />
      ) : null}

      {/* Report (task 9.2); the Exceptions tab is refined in task 9.3. */}
      {report ? <TitleAnalysisReportBody report={report} job={job} /> : null}
    </div>
  );
};

const Notice: React.FC<{
  tone: 'error' | 'info';
  message: string;
  onRetry?: () => void;
}> = ({ tone, message, onRetry }) => {
  const error = tone === 'error';
  return (
    <div
      role={error ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-xl border p-4 ${
        error ? 'border-red-200 bg-red-50' : 'border-[#EDE7FF] bg-[#F5F3FF]'
      }`}
    >
      <AlertCircle
        className={`mt-0.5 h-4 w-4 flex-shrink-0 ${error ? 'text-red-600' : 'text-[#5A21C9]'}`}
        aria-hidden="true"
      />
      <div className="flex-1">
        <p className={`text-sm ${error ? 'text-red-800' : 'text-[#5A21C9]'}`}>{message}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Reload
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default TitleAnalysisMode;
